import React from "react";
import Layout from "../../components/Layout/Layout";
import Inventory from "../../components/Inventory/Inventory";
import { Link, graphql } from "gatsby";
import { useImx } from "../../contexts/ImxContext";
import useAssets from "../../hooks/useAssets";
import { upperFirst } from "../../utils/utils";
import Weather from "../../components/Weather/Weather";
import useFarmingPlayer from "../../hooks/useFarmingPlayer";
import useFarmingFarms from "../../hooks/useFarmingFarms";
import useFarmingBunnies from "../../hooks/useFarmingBunnies";

import "../../css/farm/overview.scss";

export const farmingSubmenu = (
  <ul>
    <li>
      <Link to="/farm/mint">MINT</Link>
    </li>
    <li>
      <Link to="/farm/overview">MANAGE</Link>
    </li>
    <li>
      <Link to="/farm/hijack">HIJACK</Link>
    </li>
    <li>
      <Link to="/my/cabbage">CABBAGE</Link>
    </li>
    <li>
      <Link to="/farm/leaderboard">LEADERBOARD</Link>
    </li>
  </ul>
);

const FarmOverviewPage = ({ data: { site } }) => {
  const { account } = useImx();
  const player = useFarmingPlayer();
  const farms = useAssets("farm");
  const bunnies = useAssets("original-bunny");
  const farmingFarms = useFarmingFarms();
  const farmingBunnies = useFarmingBunnies();

  const itemsFarms =
    farms &&
    farms.map((farm) => ({
      image: farm,
      title: `${upperFirst(farm.variation.rarity)} farm`,
      amount: farm.amount,
      angled: false,
      to: `/farm/manage-farm?farmId=${farm.tokenId}`,
      status:
        farmingFarms && farmingFarms.find((f) => f.farmId === farm.tokenId)?.yield.cabbage > 0 ? (
          <>
            +{Math.round(farmingFarms.find((f) => f.farmId === farm.tokenId)?.yield.cabbage)}
            <> </>
            <img src="/assets/icons/farm-cabbage.png" alt="Cabbage" /> / day
          </>
        ) : (
          <>
            <img src="/assets/icons/close.png" alt="Not producing" />
          </>
        ),
    }));
  const itemsBunnies =
    bunnies &&
    bunnies.map((bunny) => {
      const assignment = farmingBunnies && farmingBunnies.find((b) => b.bunnyId === bunny.tokenId)?.assignment;
      return {
        image: bunny,
        title: `Bad Bunny #${bunny.tokenId}`,
        amount: 1,
        angled: false,
        to: `/farm/manage-bunny?bunnyId=${bunny.tokenId}`,
        status:
          (assignment?.post === "farmer" && (
            <>
              › <img src="/assets/icons/farm-cabbage.png" alt="Working at farm" /> ‹
            </>
          )) ||
          (assignment?.post === "guard" && (
            <>
              › <img src="/assets/icons/farm-loot.png" alt="Staged as guard" /> ‹
            </>
          )) ||
          (assignment?.post === "hijacker" && (
            <>
              › <img src="/assets/icons/hijack.png" alt="Hijacking shipments" style={{ width: 19, height: 19 }} /> ‹
            </>
          )) ||
          null,
      };
    });
  const itemsResources = [
    player &&
      player.storage.cabbage && {
        image: `/assets/images/cabbage.png`,
        title: `Cabbage`,
        amount: player.storage.cabbage,
      },
    player &&
      player.storage.onyxShards && {
        image: `/assets/images/onyx-shards.png`,
        title: `Onyx Shards`,
        amount: player.storage.onyxShards,
      },
    player &&
      player.storage.goldNuggets && {
        image: `/assets/images/gold-nuggets.png`,
        title: `Gold Nuggets`,
        amount: player.storage.goldNuggets,
      },
    player &&
      player.storage.uncutDiamonds && {
        image: `/assets/images/uncut-diamonds.png`,
        title: `Uncut Diamonds`,
        amount: player.storage.uncutDiamonds,
      },
    player &&
      player.storage.pesticide && {
        image: `/assets/images/cabbage-shop/pesticide.png`,
        title: "Pesticide",
        amount: player.storage.pesticide,
      },
    player &&
      player.storage.fertilizer && {
        image: `/assets/images/cabbage-shop/fertilizer.png`,
        title: "Fertilizer",
        amount: player.storage.fertilizer,
      },
    player &&
      player.storage.pickaxe && {
        image: `/assets/images/cabbage-shop/pickaxe.png`,
        title: "Pickaxe",
        amount: player.storage.pickaxe,
      },
    player &&
      player.storage.lockbox && {
        image: `/assets/images/cabbage-shop/lockbox.png`,
        title: "Lockbox",
        amount: player.storage.lockbox,
      },
    {
      image: "/assets/icons/add.png",
      title: "Get more resources",
      to: "/cabbage-shop",
    },
  ].filter((a) => !!a);

  return (
    <Layout title="Farming overview" siteMetadata={site.siteMetadata} submenu={farmingSubmenu} framework={true}>
      <main className="container">
        <div className="hero">
          <h2>
            <span className="highlight">FARMING</span>
          </h2>
        </div>
        {account ? (
          <div className="farming-overview">
            <div className="weather">
              <h3>Weather</h3>
              <Weather prediction={player && player.prediction} />
            </div>
            <div className="farms">
              <h3>Farms</h3>
              <Inventory items={itemsFarms} cols={4} mobileCols={2} minRows={1} smallerImages={true} />
            </div>
            <div className="bunnies">
              <h3>Bunnies</h3>
              <Inventory items={itemsBunnies} cols={4} mobileCols={2} minRows={2} smallerImages={true} />
            </div>
            <div className="resources">
              <h3>Resources</h3>
              <Inventory items={itemsResources} cols={4} mobileCols={2} minRows={1} smallerImages={true} />
            </div>
          </div>
        ) : (
          <div className="my-account-assets">
            <p>Wallet not connected</p>
          </div>
        )}
      </main>
    </Layout>
  );
};

export default FarmOverviewPage;

export const pageQuery = graphql`
  query FarmOverviewQuery {
    site {
      siteMetadata {
        author
        description
        siteUrl
        title
        keywords
      }
    }
  }
`;
